import React from 'react';
import { HistogramChartWrapper, LineChartWrapper } from '../routes/Chart/style';
import AreaChart from 'components/AreaChart';
import BarChart from 'components/BarChart';
import HistogramChart from 'components/HistogramChart';
import LineChart from 'components/LineChart';
import PieChart from 'components/PieChart';
import StackedBarChart from 'components/StackedBarChart';
import { CHART_TYPE } from 'utils/constants';
import { Series } from '../interfaces';
import utils from 'utils';
import { colorPalette } from 'utils/styleConstants';

interface ChartAreaProps {
  series: Series[];
  checkboxedRowNames?: string[];
  chartType?: string;
  selectedName?: string;
}

// --- Specific Chart Renderers ---

const LineChartRenderer = ({ series, checkboxedRowNames, selectedName }: ChartAreaProps) => {
  const selectedLineData = series.filter(
    ({ name: datasetName, average }) => checkboxedRowNames?.includes(datasetName) || average,
  );
  const colors = utils.colors.generateChartColors(selectedLineData.length, colorPalette.graphics);

  return (
    <LineChartWrapper>
      <LineChart
        datasets={selectedLineData}
        colors={colors}
        showAverage
        selectedDatasetName={selectedName}
        key={selectedLineData.length}
      />
    </LineChartWrapper>
  );
};

const HistogramChartRenderer = ({ series, checkboxedRowNames }: ChartAreaProps) => {
  const selectedHistogramData = series
    .filter(({ name }) => checkboxedRowNames?.includes(name))
    .flatMap(({ data }) => data);

  return (
    <HistogramChartWrapper addExtraPadding={selectedHistogramData.length <= 0}>
      <HistogramChart
        data={selectedHistogramData}
        key={`${selectedHistogramData.length}_${selectedHistogramData[0]?.x}`}
      />
    </HistogramChartWrapper>
  );
};

const MultiBarChartRenderer = ({ series, checkboxedRowNames }: ChartAreaProps) => {
  const selectedMultiBarData = series.map((serie) => ({
    ...serie,
    data: serie.data.filter(({ x }: { x: string }) => checkboxedRowNames?.includes(x)),
  }));
  const colors = utils.colors.generateChartColors(series.length, colorPalette.graphics);

  return (
    <HistogramChartWrapper addExtraPadding={selectedMultiBarData[0].data.length <= 0}>
      <BarChart
        datasets={selectedMultiBarData}
        key={`${selectedMultiBarData[0].data.length}_${selectedMultiBarData[0].data?.[0]?.x}`}
        colors={colors}
      />
    </HistogramChartWrapper>
  );
};

const PieChartRenderer = ({ series, checkboxedRowNames }: ChartAreaProps) => {
  const selectedPieData = series[0].data.filter((item: { x: string }) =>
    checkboxedRowNames?.some((name) => item.x === name),
  );
  const colors = utils.colors.generateChartColors(selectedPieData.length, colorPalette.graphics);

  return (
    <LineChartWrapper>
      <PieChart data={selectedPieData} colors={colors} key={selectedPieData.length} />
    </LineChartWrapper>
  );
};

const StackedBarChartRenderer = ({ series, checkboxedRowNames }: ChartAreaProps) => {
  const selectedStackedChartData = series.filter(({ name: datasetName }) => checkboxedRowNames?.includes(datasetName));
  const colors = utils.colors.generateChartColors(selectedStackedChartData.length, colorPalette.graphics);

  return (
    <LineChartWrapper>
      <StackedBarChart datasets={selectedStackedChartData} colors={colors} key={selectedStackedChartData.length} />
    </LineChartWrapper>
  );
};

const AreaChartRenderer = ({ series, checkboxedRowNames }: ChartAreaProps) => {
  const selectedAreaData = series.filter(({ name: datasetName }) => checkboxedRowNames?.includes(datasetName));
  const colors = utils.colors.generateChartColors(selectedAreaData.length, colorPalette.graphics);

  return (
    <LineChartWrapper>
      <AreaChart datasets={selectedAreaData as any} colors={colors} key={selectedAreaData.length} />
    </LineChartWrapper>
  );
};

// --- Chart Map Registry ---
const chartRegistry = {
  [CHART_TYPE.line]: LineChartRenderer,
  [CHART_TYPE.histogram]: HistogramChartRenderer,
  [CHART_TYPE.bar]: MultiBarChartRenderer,
  [CHART_TYPE.pie]: PieChartRenderer,
  [CHART_TYPE.stackedBar]: StackedBarChartRenderer,
  [CHART_TYPE.area]: AreaChartRenderer,
};

const ChartArea = (props: ChartAreaProps) => {
  if (!props.series || !props.chartType) return null;
  const normalizedType = props.chartType.toLowerCase();
  const ChartComponent = chartRegistry[normalizedType];

  if (!ChartComponent) return null;

  return <ChartComponent {...props} />;
};

export default ChartArea;
